import '@app/common/array-extensions';
import { UserLanguageTerm } from '@app/models/user-language-term';

/**
 * Will filter the elements so that the items languages matches at least on of the user languages
 * @param elements the elements to be filtered
 * @param elementLanguagePropertyName the name of the property where the languages resides. Can be nested for example "AppTile.LanguageTermAppTiles"
 * @param userLanguages an array of the users languages
 * @param showItemsWithoutLanguage if set to true, items without any specified languages should be shown to all. Otherwise they will be hidden
 * @param defaultLanguageTermId If specified, the termGuid will be used as a default language for users who haven't selected any languages.
 */
export function applyLanguageFilteringMulti<T>(elements: T[], elementLanguagePropertyName: string, userLanguages: UserLanguageTerm[], showItemsWithoutLanguage = true, defaultLanguageTermId = null) {

  // If empty, return right away
  if (elements.length === 0) {
    return elements;
  }

  // If the user doesn't have any selected languages, filter the elements based on the defaultLanguageTermId if specified
  if (defaultLanguageTermId && userLanguages.length === 0) {
    userLanguages.push(new UserLanguageTerm({ LanguageTermId: defaultLanguageTermId }));
  }

  const filteredElements = elements.filter(element => {
    const elementLanguages = getNestedObject(element, elementLanguagePropertyName);
    // if there isn't any languages on the item, return true or false based on setting showItemsWithoutLanguage
    if (elementLanguages.length === 0) {
      return showItemsWithoutLanguage;
    }

    // get the element languages that matches any of the users languages
    const userElementLanguage = elementLanguages.filter(l => userLanguages.some(ul => ul.LanguageTermId === l['LanguageTermId']));
    return userElementLanguage.length > 0;
  });
  return filteredElements;
}

/**
 * Will filter the elements so that the item language matches any of the users languages
 * @param elements the elements to be filtered
 * @param userLanguages an array of the users languages
 * @param showItemsWithoutLanguage if set to true, items without any specified languages should be shown to all. Otherwise they will be hidden
 * @param defaultLanguageTermId If specified, the termGuid will be used as a default language for users who haven't selected any languages.
 */
export function applyLanguageFilteringSingle<T>(elements: T[], userLanguages: UserLanguageTerm[], showItemsWithoutLanguage = true, defaultLanguageTermId = null) {

  // If empty, return right away
  if (elements.length === 0) {
    return elements;
  }

  // If the user doesn't have any selected languages, filter the elements based on the defaultLanguageTermId if specified
  if (defaultLanguageTermId && userLanguages.length === 0) {
    userLanguages.push(new UserLanguageTerm({ LanguageTermId: defaultLanguageTermId }));
  }

  const filteredElements = elements.filter(element => {
    if (element['LanguageTermId']) {
      return userLanguages.some(ul => ul.LanguageTermId === element['LanguageTermId']);
    }
    // if there isn't any languages on the item, return true or false based on setting showItemsWithoutLanguage
    return showItemsWithoutLanguage;
  });
  return filteredElements;
}

const getNestedObject = (nestedObj, pathArr: string) => {
  return pathArr.split('.').reduce((obj, key) =>
    (obj && obj[key] !== 'undefined') ? obj[key] : undefined, nestedObj);
};
